import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useGridCopyMenu } from './gridCommon';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface SimRun {
  id: number;
  prodid: string;
  strategy_code: string;
  start_d: string;
  end_d: string;
  fast_period: number;
  slow_period: number;
  adx_period: number;
  adx_threshold: number;
  initial_capital_krw: number;
  day_rows: number;
  created_at: string;
  note: string | null;
}

interface SimDay {
  d: string;
  o: number; h: number; l: number; c: number; v: number;
  ema_fast: number | null;
  ema_slow: number | null;
  adx: number | null;
  plus_di: number | null;
  minus_di: number | null;
  signal: string | null;
  invested: boolean;
  cash_krw: number;
  shares: number;
  position_value_krw: number;
  total_equity_krw: number;
  daily_pnl_krw: number;
  daily_pnl_pct: number;
  trade_action: string | null;
  trade_qty: number | null;
  trade_price: number | null;
  trade_value_krw: number | null;
  strat_bar_ret: number | null;
  cum_strat_equity: number | null;
}

interface SummaryResp {
  run: SimRun;
  stat: {
    total_days: number;
    buy_count: number;
    sell_count: number;
    long_days: number;
    flat_days: number;
    first_d: string;
    last_d: string;
    min_equity: number;
    max_equity: number;
    final_equity: number;
    sum_pnl: number;
  };
}

const fmtDate = (s: string | null) => s ? s.slice(0, 10) : '';
const fmtNum = (n: any) => (n == null ? '' : Number(n).toLocaleString());
const fmtPct = (n: any) => (n == null ? '' : `${Number(n).toFixed(2)}%`);

const QuantCursor: React.FC = () => {
  const axios = getMainAxios();
  const copyMenu = useGridCopyMenu();
  const [runs, setRuns] = useState<SimRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<SimRun | null>(null);
  const [days, setDays] = useState<SimDay[]>([]);
  const [summary, setSummary] = useState<SummaryResp | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<SimRun[]>('/api/v1/quant-cursor/runs');
      setRuns(res.data ?? []);
    } catch (e) {
      console.error('runs 조회 실패', e);
    } finally {
      setLoading(false);
    }
  }, [axios]);

  const fetchRunDetail = useCallback(async (run: SimRun) => {
    setSelectedRun(run);
    setLoading(true);
    try {
      const [daysRes, sumRes] = await Promise.all([
        axios.get<SimDay[]>(`/api/v1/quant-cursor/run/${run.id}/days`),
        axios.get<SummaryResp>(`/api/v1/quant-cursor/run/${run.id}/summary`),
      ]);
      setDays(daysRes.data ?? []);
      setSummary(sumRes.data);
    } catch (e) {
      console.error('run detail 조회 실패', e);
      setDays([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [axios]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    if (runs.length > 0 && !selectedRun) {
      fetchRunDetail(runs[0]);
    }
  }, [runs, selectedRun, fetchRunDetail]);

  const runColDefs = useMemo<ColDef<SimRun>[]>(() => [
    { headerName: 'ID', field: 'id', width: 70, cellStyle: { textAlign: 'right' } },
    { headerName: '종목', field: 'prodid', width: 90 },
    { headerName: '전략', field: 'strategy_code', flex: 1, minWidth: 160 },
    { headerName: '시작일', field: 'start_d', width: 110, valueFormatter: p => fmtDate(p.value) },
    { headerName: '종료일', field: 'end_d',   width: 110, valueFormatter: p => fmtDate(p.value) },
    { headerName: 'Fast', field: 'fast_period', width: 70, cellStyle: { textAlign: 'right' } },
    { headerName: 'Slow', field: 'slow_period', width: 70, cellStyle: { textAlign: 'right' } },
    { headerName: 'ADX', field: 'adx_period', width: 70, cellStyle: { textAlign: 'right' } },
    { headerName: 'ADX임계', field: 'adx_threshold', width: 80, cellStyle: { textAlign: 'right' } },
    { headerName: '초기자본', field: 'initial_capital_krw', width: 120,
      valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '일수', field: 'day_rows', width: 80, cellStyle: { textAlign: 'right' } },
    { headerName: '생성일', field: 'created_at', width: 150,
      valueFormatter: p => p.value ? p.value.slice(0, 19).replace('T', ' ') : '' },
    { headerName: '비고', field: 'note', flex: 1, minWidth: 140 },
  ], []);

  const dayColDefs = useMemo<ColDef<SimDay>[]>(() => [
    { headerName: '일자', field: 'd', width: 110, pinned: 'left', valueFormatter: p => fmtDate(p.value) },
    { headerName: '시', field: 'o', width: 85, valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '고', field: 'h', width: 85, valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '저', field: 'l', width: 85, valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '종', field: 'c', width: 85, valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '거래량', field: 'v', width: 110, valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: 'EMA-F', field: 'ema_fast', width: 90,
      valueFormatter: p => p.value ? Number(p.value).toFixed(1) : '',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: 'EMA-S', field: 'ema_slow', width: 90,
      valueFormatter: p => p.value ? Number(p.value).toFixed(1) : '',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: 'ADX', field: 'adx', width: 75,
      valueFormatter: p => p.value ? Number(p.value).toFixed(1) : '',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '+DI', field: 'plus_di', width: 75,
      valueFormatter: p => p.value ? Number(p.value).toFixed(1) : '',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '-DI', field: 'minus_di', width: 75,
      valueFormatter: p => p.value ? Number(p.value).toFixed(1) : '',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '신호', field: 'signal', width: 80,
      cellStyle: p => p.value === 'LONG' ? { color: '#065f46', fontWeight: 600 } : { color: '#6b7280' } },
    { headerName: '체결', field: 'trade_action', width: 80,
      cellStyle: p => p.value === 'BUY' ? { color: '#1e40af', fontWeight: 700 }
                    : p.value === 'SELL' ? { color: '#991b1b', fontWeight: 700 } : undefined },
    { headerName: '체결수량', field: 'trade_qty', width: 90, valueFormatter: p => fmtNum(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '체결가', field: 'trade_price', width: 100, valueFormatter: p => fmtNum(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '보유주', field: 'shares', width: 90, valueFormatter: p => fmtNum(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '현금', field: 'cash_krw', width: 130, valueFormatter: p => fmtNum(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '평가금액', field: 'position_value_krw', width: 130, valueFormatter: p => fmtNum(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '총자산', field: 'total_equity_krw', width: 140, valueFormatter: p => fmtNum(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 } },
    { headerName: '일손익', field: 'daily_pnl_krw', width: 110, valueFormatter: p => fmtNum(p.value),
      cellStyle: p => ({ textAlign: 'right', fontFamily: 'monospace',
        color: (p.value ?? 0) > 0 ? '#065f46' : (p.value ?? 0) < 0 ? '#991b1b' : undefined }) },
    { headerName: '일손익%', field: 'daily_pnl_pct', width: 90,
      valueFormatter: p => p.value != null ? `${Number(p.value).toFixed(2)}` : '',
      cellStyle: p => ({ textAlign: 'right', fontFamily: 'monospace',
        color: (p.value ?? 0) > 0 ? '#065f46' : (p.value ?? 0) < 0 ? '#991b1b' : undefined }) },
  ], []);

  // 차트 옵션
  const chartOptions = useMemo<Highcharts.Options>(() => {
    if (days.length === 0) return {};
    const dates = days.map(d => new Date(d.d).getTime());
    const initial = summary?.run.initial_capital_krw ?? 10_000_000;

    // 매매 시점 마커
    const trades = days
      .filter(d => d.trade_action === 'BUY' || d.trade_action === 'SELL')
      .map(d => ({
        x: new Date(d.d).getTime(),
        y: d.c,
        marker: {
          symbol: d.trade_action === 'BUY' ? 'triangle' : 'triangle-down',
          fillColor: d.trade_action === 'BUY' ? '#1e40af' : '#991b1b',
          lineColor: '#fff', lineWidth: 1, radius: 6,
        },
        action: d.trade_action,
      }));

    return {
      chart: { height: 520, zooming: { type: 'x' } },
      title: { text: `${summary?.run.prodid} — ${summary?.run.strategy_code} (run #${summary?.run.id})`,
        style: { fontSize: '13px', color: '#1e40af' } },
      xAxis: {
        type: 'datetime',
        crosshair: true,
        labels: {
          formatter: function () {
            const d = new Date(this.value as number);
            const yy = String(d.getFullYear()).slice(-2);
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yy}.${mm}.${dd}`;
          },
        },
        dateTimeLabelFormats: {
          day: '%y.%m.%d', week: '%y.%m.%d', month: '%y.%m.%d', year: '%y.%m.%d',
        },
      },
      yAxis: [
        { title: { text: '가격 (KRW)', style: { fontSize: '11px' } }, height: '50%' },
        { title: { text: '총자산 (KRW)', style: { fontSize: '11px' } }, top: '55%', height: '45%', offset: 0,
          plotLines: [{ value: initial, color: '#9ca3af', dashStyle: 'Dash', width: 1,
            label: { text: '초기자본', style: { color: '#6b7280', fontSize: '10px' } } }],
        },
      ],
      legend: { itemStyle: { fontSize: '11px' } },
      tooltip: { shared: true, valueDecimals: 0, split: false,
        formatter: function (this: any) {
          const dt = new Date(this.x).toISOString().slice(0, 10);
          let out = `<b>${dt}</b><br/>`;
          this.points?.forEach((p: any) => {
            out += `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y?.toLocaleString()}</b><br/>`;
          });
          return out;
        },
      },
      plotOptions: { series: { animation: false, marker: { enabled: false } } },
      series: [
        { type: 'line', name: '종가', yAxis: 0, color: '#374151',
          data: days.map((d, i) => [dates[i], Number(d.c)]) },
        { type: 'line', name: `EMA${summary?.run.fast_period}`, yAxis: 0, color: '#3b82f6', lineWidth: 1,
          data: days.map((d, i) => [dates[i], d.ema_fast ? Number(d.ema_fast) : null]) },
        { type: 'line', name: `EMA${summary?.run.slow_period}`, yAxis: 0, color: '#f59e0b', lineWidth: 1,
          data: days.map((d, i) => [dates[i], d.ema_slow ? Number(d.ema_slow) : null]) },
        { type: 'scatter', name: '매매시점', yAxis: 0,
          data: trades as any,
          tooltip: { pointFormat: '<b>{point.action}</b> @ {point.y:,.0f}' } },
        { type: 'area', name: '총자산', yAxis: 1, color: '#10b981',
          fillOpacity: 0.15, lineWidth: 1.5,
          data: days.map((d, i) => [dates[i], Number(d.total_equity_krw)]) },
      ] as any,
      credits: { enabled: false },
    };
  }, [days, summary]);

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>

      {/* 헤더 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '16px', marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#1e40af', fontSize: '14px', fontWeight: 600 }}>
            Quant(커서) — SamsungResearchSim 결과
          </h3>
          <button onClick={fetchRuns} disabled={loading} style={{
            padding: '4px 12px', background: '#3b82f6', color: 'white',
            border: 'none', borderRadius: '4px', fontSize: '12px',
            cursor: loading ? 'wait' : 'pointer',
          }}>
            {loading ? '⏳' : '🔄'} 새로고침
          </button>
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#6b7280' }}>
            실행 목록 {runs.length}건
          </span>
        </div>
      </div>

      {/* 실행 목록 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '16px', marginBottom: '20px',
      }}>
        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
          시뮬 실행 목록 — 행 클릭 시 하단에 일자별 데이터 + 차트
        </div>
        <div className="ag-theme-quartz" style={{
          height: Math.min(360, Math.max(120, (runs.length + 1) * 32 + 8)),
          width: '100%', fontSize: '12px',
        }}>
          <AgGridReact<SimRun>
            rowData={runs}
            columnDefs={runColDefs}
            defaultColDef={{ sortable: true, filter: true, resizable: true }}
            onRowClicked={e => e.data && fetchRunDetail(e.data)}
            getRowStyle={p => p.data?.id === selectedRun?.id ? { backgroundColor: '#dbeafe' } : undefined}
            rowHeight={30}
            headerHeight={32}
            {...copyMenu.gridProps}
          />
        </div>
      </div>

      {/* 요약 + 차트 */}
      {summary && (
        <div style={{
          backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
          padding: '16px', marginBottom: '20px',
        }}>
          <div style={{ marginBottom: '10px', display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: '12px' }}>
            <span style={{ color: '#1e40af', fontWeight: 600 }}>요약</span>
            <span>총 일수 <b style={{ color: '#1e40af' }}>{summary.stat.total_days}</b></span>
            <span>매수 <b style={{ color: '#1e40af' }}>{summary.stat.buy_count}</b></span>
            <span>매도 <b style={{ color: '#991b1b' }}>{summary.stat.sell_count}</b></span>
            <span>롱 보유일 <b>{summary.stat.long_days}</b></span>
            <span>플랫일 <b style={{ color: '#6b7280' }}>{summary.stat.flat_days}</b></span>
            <span>초기자본 <b>{fmtNum(summary.run.initial_capital_krw)}</b></span>
            <span>최종자산 <b style={{
              color: summary.stat.final_equity >= summary.run.initial_capital_krw ? '#065f46' : '#991b1b'
            }}>{fmtNum(summary.stat.final_equity)}</b></span>
            <span>총손익 <b style={{
              color: (summary.stat.sum_pnl ?? 0) >= 0 ? '#065f46' : '#991b1b'
            }}>{fmtNum(summary.stat.sum_pnl)}</b></span>
            <span>수익률 <b style={{
              color: summary.stat.final_equity >= summary.run.initial_capital_krw ? '#065f46' : '#991b1b'
            }}>{fmtPct(((summary.stat.final_equity - summary.run.initial_capital_krw) / summary.run.initial_capital_krw) * 100)}</b></span>
          </div>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
      )}

      {/* 일자별 데이터 */}
      {selectedRun && (
        <div style={{
          backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
          padding: '16px', marginBottom: '20px',
        }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
            일자별 상세 — run #{selectedRun.id} ({selectedRun.prodid}) · {days.length}일
          </div>
          <div className="ag-theme-quartz" style={{ height: 480, width: '100%', fontSize: '11px' }}>
            <AgGridReact<SimDay>
              rowData={days}
              columnDefs={dayColDefs}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowHeight={26}
              headerHeight={30}
              {...copyMenu.gridProps}
            />
          </div>
        </div>
      )}

      {copyMenu.menu}
    </div>
  );
};

export default QuantCursor;
